<!-- The brand corner: mark, name, and what this application is. Purely
     presentational — the Dashboard link below is the way home. -->
<div class="sidebar__brand">
  <!-- The brand mark: the Saphir gem tile from Docs/brand (logo-tile.svg), flattened for 32px. -->
  <svg class="sidebar__logo" viewBox="0 0 100 100" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="swLogoBg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#1a3228" />
        <stop offset="1" stop-color="#0a1a12" />
      </linearGradient>
    </defs>
    <rect width="100" height="100" rx="20" fill="url(#swLogoBg)" />
    <polygon points="30,14 70,14 62,46 38,46" fill="#a8e6c6" />
    <polygon points="4,46 30,14 38,46" fill="#54b083" />
    <polygon points="62,46 70,14 96,46" fill="#3d9068" />
    <polygon points="4,46 38,46 50,96" fill="#54b083" />
    <polygon points="38,46 62,46 50,96" fill="#1e5a3f" />
    <polygon points="62,46 96,46 50,96" fill="#2a7050" />
    <g
      fill="none"
      stroke="#ffffff"
      stroke-opacity="0.35"
      stroke-width="1.5"
      stroke-linejoin="round"
    >
      <polyline points="4,46 96,46" />
      <polyline points="30,14 38,46 50,96 62,46 70,14" />
    </g>
  </svg>
  @if (!rail()) {
    <span class="sidebar__brand-text">
      <span class="sidebar__brand-name">{{ 'app.title' | translate }}</span>
      <span class="sidebar__brand-sub">{{ 'app.subtitle' | translate }}</span>
    </span>
  }
</div>

<!-- The company the user works AS. A selector whenever there is one at all —
     including a single company: which company the data belongs to is worth
     seeing at a glance, and a control that appears and disappears with the
     number of memberships makes the column shift for no reason the user can
     predict. Nothing while UMC gave no answer. Switching re-evaluates every
     permission the modules check. The rail has no room for it — the full
     column returns it. -->
@if (!rail()) {
  @if (companies.memberships().length >= 1) {
    <div class="sidebar__company">
      <!-- The customer first, the tenant under it: the business the user works
           for is the headline, the branch is the choice inside it. Absent while
           UMC sends no customer name. -->
      @if (companies.activeCustomerName(); as customerName) {
        <div class="sidebar__customer">
          <span class="sidebar__customer-mark" aria-hidden="true">{{ customerInitials() }}</span>
          <span class="sidebar__customer-text">
            <span class="sidebar__customer-caption">{{ 'sidebar.customer' | translate }}</span>
            <span class="sidebar__customer-name" [title]="customerName">{{ customerName }}</span>
          </span>
        </div>
      }

      <!-- `optionDisabled` greys out a company that holds no licence for the
           module currently open: switching there would only bounce the user
           back to the dashboard. The reason travels with the row as a tooltip
           and a short marker, so the state is never mute. -->
      <p-select
        class="sidebar__company-select"
        size="small"
        [options]="companyOptions()"
        optionLabel="companyName"
        optionValue="companyId"
        optionDisabled="unlicensed"
        [ngModel]="companies.activeMembership()?.companyId ?? null"
        (ngModelChange)="switchCompany($event)"
        [attr.aria-label]="'header.company' | translate"
      >
        <ng-template #selectedItem let-selected>
          <span class="sidebar__company-option">
            <span
              class="sidebar__company-dot"
              [style.background]="selected?.companyColor ?? 'var(--sw-brand)'"
            ></span>
            {{ selected?.companyName }}
          </span>
        </ng-template>
        <ng-template #item let-membership>
          <span
            class="sidebar__company-option"
            [class.sidebar__company-option--unlicensed]="membership.unlicensed"
            [pTooltip]="membership.unlicensed ? ('sidebar.company.unlicensed' | translate) : ''"
            tooltipPosition="right"
          >
            <span
              class="sidebar__company-dot"
              [style.background]="membership.companyColor ?? 'var(--sw-brand)'"
            ></span>
            {{ membership.companyName }}
            @if (membership.unlicensed) {
              <span class="sidebar__no-licence">
                {{ 'sidebar.company.noLicence' | translate }}
              </span>
            }
          </span>
        </ng-template>
      </p-select>
    </div>
  }
}

<nav class="sidebar" [attr.aria-label]="'sidebar.label' | translate">
  <ul class="sidebar__list">
    <li>
      <!-- Straight to /modules rather than /: the root only redirects there,
           and routerLinkActive matches the address after the redirect. -->
      <a
        class="sidebar__link"
        [routerLink]="['/', prefix]"
        routerLinkActive="sidebar__link--active"
        [routerLinkActiveOptions]="{ exact: true }"
        [attr.aria-label]="'sidebar.dashboard' | translate"
        [pTooltip]="'sidebar.dashboard' | translate"
        [tooltipDisabled]="!rail()"
        tooltipPosition="right"
      >
        <i class="sidebar__icon pi pi-home" aria-hidden="true"></i>
        <span class="sidebar__text">{{ 'sidebar.dashboard' | translate }}</span>
      </a>
    </li>

    <li class="sidebar__divider" role="presentation"></li>

    <!-- Until UMC has answered, no module is known to be openable: placeholder
         rows hold the space instead of entries that might vanish a moment later. -->
    @if (!licensesSettled()) {
      @for (row of skeletonRows(); track $index) {
        <li class="sidebar__skeleton" aria-hidden="true">
          <span class="sidebar__skeleton-icon"></span>
          @if (!rail()) {
            <span class="sidebar__skeleton-label"></span>
          }
        </li>
      }
    }

    @for (module of modules(); track module.id) {
      <!-- The flyout follows the pointer and the focus into the module, and is
           dismissed the moment a destination is picked — see the class doc. -->
      <li
        class="sidebar__module"
        [class.sidebar__module--flyout-open]="flyoutOpen() === module.id"
        (mouseenter)="showFlyout(module.id)"
        (mouseleave)="hideFlyout(module.id)"
        (focusin)="showFlyout(module.id)"
        (focusout)="onFlyoutFocusOut($event, module.id)"
      >
        <div class="sidebar__module-row">
          <a
            class="sidebar__link sidebar__link--module"
            [routerLink]="link(module.id)"
            routerLinkActive="sidebar__link--active"
            [routerLinkActiveOptions]="{ exact: true }"
            [attr.aria-label]="module.titleKey | translate"
            [pTooltip]="module.titleKey | translate"
            [tooltipDisabled]="!rail() || module.items.length > 0"
            tooltipPosition="right"
            (click)="onDestinationPicked(module.id)"
          >
            <i class="sidebar__icon" [class]="module.icon" aria-hidden="true"></i>
            <span class="sidebar__text">{{ module.titleKey | translate }}</span>
          </a>

          @if (!rail() && (module.items.length > 0 || module.headings.length > 0)) {
            <button
              type="button"
              class="sidebar__toggle"
              [attr.aria-expanded]="expanded(module.id)"
              [attr.aria-controls]="'sidebar-section-' + module.id"
              [attr.aria-label]="'sidebar.toggleSection' | translate"
              (click)="toggle(module.id)"
            >
              <i
                class="pi"
                [class.pi-chevron-down]="expanded(module.id)"
                [class.pi-chevron-right]="!expanded(module.id)"
                aria-hidden="true"
              ></i>
            </button>
          }
        </div>

        <!-- In the full column the list sits under the module when unfolded;
             on the rail it stays in the DOM and CSS turns it into a flyout
             beside the icon, shown on hover or keyboard focus. -->
        @if (
          (rail() || expanded(module.id)) &&
          (module.items.length > 0 || module.headings.length > 0)
        ) {
          <ul class="sidebar__list sidebar__list--sub" [id]="'sidebar-section-' + module.id">
            @if (rail()) {
              <!-- The flyout names its module — the tooltip that would have
                   done it is off while the flyout answers to the hover. -->
              <li class="sidebar__flyout-title">{{ module.titleKey | translate }}</li>
            }

            <!-- A module whose menu the user arranged sends headings; every
                 other module sends the plain tree it always has. Both shapes
                 are rendered by the same entry template, so an entry looks
                 and behaves the same wherever it sits. -->
            @if (module.headings.length > 0) {
              @for (heading of module.headings; track heading.id) {
                @let open = headingOpen(module.id, heading);
                @let untitled = isUntitled(heading);
                <li
                  class="sidebar__heading"
                  [class.sidebar__heading--fixed]="heading.fixed"
                  [class.sidebar__heading--untitled]="untitled"
                  [class.sidebar__heading--drop]="dropHeading() === heading.id"
                  [attr.data-accent]="heading.accent ?? null"
                  (dragover)="!heading.entriesFixed && onDragOverHeading($event, heading.id)"
                  (drop)="!heading.entriesFixed && onDrop($event, module.id, heading)"
                >
                  <!-- On the rail everything is shown at once, so the caption
                       is a caption; in the column it is the fold control.
                       An untitled heading has neither: its entries stand
                       directly in the column, always open. -->
                  @if (untitled) {
                    <!-- No caption, no fold control. -->
                  } @else if (rail()) {
                    <span class="sidebar__heading-label">{{ heading.title }}</span>
                  } @else {
                    <div class="sidebar__heading-row">
                      <!-- The chevron leads rather than trails: it is about
                           hierarchy, and hierarchy in this column reads from
                           the left, where the accent rule already anchors the
                           heading. That leaves the trailing edge to the
                           actions alone, so a long title cannot push the two
                           into each other. -->
                      <button
                        type="button"
                        class="sidebar__heading-label"
                        [attr.aria-expanded]="open"
                        [attr.title]="heading.title"
                        (click)="toggleHeading(module.id, heading)"
                      >
                        <i
                          class="sidebar__heading-chevron pi"
                          [class.pi-chevron-down]="open"
                          [class.pi-chevron-right]="!open"
                          aria-hidden="true"
                        ></i>
                        <span class="sidebar__text">{{ heading.title }}</span>
                      </button>

                      <!-- One control for everything a heading can do; the
                           panel below lists them. Out of sight until hover or
                           focus — the column is navigation first. -->
                      @if (canEditHeading(module.id, heading)) {
                        @let headingPanel = 'heading:' + module.id + '/' + heading.id;
                        <button
                          type="button"
                          class="sidebar__arrange-trigger"
                          [attr.aria-expanded]="isPanelOpen(headingPanel)"
                          [attr.aria-label]="'sidebar.arrange.heading' | translate"
                          [pTooltip]="'sidebar.arrange.heading' | translate"
                          tooltipPosition="right"
                          (click)="togglePanel(headingPanel, $event)"
                        >
                          <i class="pi pi-ellipsis-h" aria-hidden="true"></i>
                        </button>
                      }
                    </div>

                    @if (isPanelOpen('heading:' + module.id + '/' + heading.id)) {
                      <div class="sidebar__arrange" role="group">
                        @if (canRename(module.id)) {
                          <label class="sidebar__arrange-field">
                            <span class="sidebar__arrange-caption">
                              {{ 'sidebar.arrange.rename' | translate }}
                            </span>
                            <input
                              #renameInput
                              type="text"
                              class="sidebar__arrange-input"
                              [value]="heading.title"
                              (keydown.enter)="
                                $event.stopPropagation();
                                renameHeading(module.id, heading.id, renameInput.value)
                              "
                            />
                          </label>
                        }

                        @if (canMoveHeading(module.id)) {
                          <div class="sidebar__arrange-row">
                            <button
                              type="button"
                              class="sidebar__arrange-action"
                              [disabled]="isFirstHeading(module.id, heading)"
                              (click)="moveHeading(module.id, heading.id, -1)"
                            >
                              <i class="pi pi-arrow-up" aria-hidden="true"></i>
                              {{ 'sidebar.arrange.up' | translate }}
                            </button>
                            <button
                              type="button"
                              class="sidebar__arrange-action"
                              [disabled]="isLastHeading(module.id, heading)"
                              (click)="moveHeading(module.id, heading.id, 1)"
                            >
                              <i class="pi pi-arrow-down" aria-hidden="true"></i>
                              {{ 'sidebar.arrange.down' | translate }}
                            </button>
                          </div>
                        }

                        @if (canSetAccent(module.id)) {
                          <div class="sidebar__arrange-row">
                            @for (choice of accentChoices; track choice) {
                              <button
                                type="button"
                                class="sidebar__accent-choice"
                                [attr.data-accent]="choice"
                                [class.sidebar__accent-choice--on]="heading.accent === choice"
                                [attr.aria-label]="'sidebar.accent.' + choice | translate"
                                [pTooltip]="'sidebar.accent.' + choice | translate"
                                tooltipPosition="top"
                                (click)="setHeadingAccent(module.id, heading.id, choice)"
                              ></button>
                            }
                            <button
                              type="button"
                              class="sidebar__accent-choice sidebar__accent-choice--none"
                              [class.sidebar__accent-choice--on]="!heading.accent"
                              [attr.aria-label]="'sidebar.accent.none' | translate"
                              [pTooltip]="'sidebar.accent.none' | translate"
                              tooltipPosition="top"
                              (click)="setHeadingAccent(module.id, heading.id, null)"
                            ></button>
                          </div>
                        }

                        @if (canDeleteHeading(module.id)) {
                          <button
                            type="button"
                            class="sidebar__arrange-action sidebar__arrange-action--danger"
                            (click)="deleteHeading(module.id, heading.id)"
                          >
                            <i class="pi pi-trash" aria-hidden="true"></i>
                            {{ 'sidebar.arrange.delete' | translate }}
                          </button>
                          <!-- Says what delete does BEFORE it is pressed: its
                               entries move, none is lost. -->
                          <p class="sidebar__arrange-note">
                            {{ 'sidebar.arrange.deleteNote' | translate }}
                          </p>
                        }
                      </div>
                    }
                  }

                  <!-- The fixed heading carries no actions and says so by
                       having none — the standalone menu does the same. The
                       sentence that used to explain it cost three lines in a
                       15.5rem column to answer a question nobody asked. -->


                  @if (open && heading.entries.length > 0) {
                    <ul class="sidebar__list sidebar__list--sub sidebar__heading-entries">
                      <ng-container
                        *ngTemplateOutlet="
                          tree;
                          context: {
                            items: heading.entries,
                            moduleId: module.id,
                            depth: 0,
                            path: headingKey(module.id, heading),
                            heading: heading,
                          }
                        "
                      />
                    </ul>
                  }
                </li>
              }
            } @else {
              <ng-container
                *ngTemplateOutlet="
                  tree;
                  context: { items: module.items, moduleId: module.id, depth: 0, path: module.id }
                "
              />
            }

            <!-- Adding a heading belongs to the whole menu, so it sits at the
                 end of the block with reset — not on any one heading. -->
            @if (canArrange(module.id) && module.headings.length > 0) {
              <li class="sidebar__menu-actions">
                @let newHeading = 'new-heading:' + module.id;
                <button
                  type="button"
                  class="sidebar__arrange-trigger sidebar__menu-action"
                  [attr.aria-expanded]="isPanelOpen(newHeading)"
                  (click)="togglePanel(newHeading, $event)"
                >
                  <i class="pi pi-plus" aria-hidden="true"></i>
                  {{ 'sidebar.arrange.newHeading' | translate }}
                </button>

                @if (isPanelOpen(newHeading)) {
                  <div class="sidebar__arrange" role="group">
                    <label class="sidebar__arrange-field">
                      <span class="sidebar__arrange-caption">
                        {{ 'sidebar.arrange.headingName' | translate }}
                      </span>
                      <!-- `$event.stopPropagation()`: the trigger that opened
                           this panel still has focus in the keyboard path, and
                           an Enter that keeps travelling re-opens the panel
                           that was just closed. -->
                      <input
                        #newHeadingInput
                        type="text"
                        class="sidebar__arrange-input"
                        (keydown.enter)="
                          $event.stopPropagation(); createHeading(module.id, newHeadingInput.value)
                        "
                      />
                    </label>
                  </div>
                }

                <!-- Reset is a rare, whole-menu action: set apart so it is
                     never one slip away from a routine one. -->
                @if (canReset(module.id)) {
                  @let resetPanel = 'reset:' + module.id;
                  <button
                    type="button"
                    class="sidebar__arrange-trigger sidebar__menu-action sidebar__menu-action--reset"
                    [attr.aria-expanded]="isPanelOpen(resetPanel)"
                    (click)="togglePanel(resetPanel, $event)"
                  >
                    <i class="pi pi-replay" aria-hidden="true"></i>
                    {{ 'sidebar.arrange.reset' | translate }}
                  </button>

                  @if (isPanelOpen(resetPanel)) {
                    <!-- Asked for, because reset discards everything the user
                         arranged and no single step undoes it. -->
                    <div class="sidebar__arrange" role="group">
                      <p class="sidebar__arrange-note">
                        {{ 'sidebar.arrange.resetNote' | translate }}
                      </p>
                      <button
                        type="button"
                        class="sidebar__arrange-action sidebar__arrange-action--danger"
                        (click)="resetMenu(module.id)"
                      >
                        {{ 'sidebar.arrange.resetConfirm' | translate }}
                      </button>
                    </div>
                  }
                }
              </li>
            }

            <!-- The module's service page (version, UMC module, licence) —
                 a shell screen, so the link is built here and not part of the
                 module's own navigation. -->
            <li>
              <a
                class="sidebar__link sidebar__link--sub sidebar__link--service"
                [style.--depth]="0"
                [routerLink]="['/', prefix, 'info', module.id]"
                routerLinkActive="sidebar__link--active"
                (click)="onDestinationPicked(module.id)"
              >
                <i class="sidebar__icon pi pi-info-circle" aria-hidden="true"></i>
                <span class="sidebar__text">{{ 'module.info.title' | translate }}</span>
              </a>
            </li>
          </ul>
        }
      </li>
    }
  </ul>
</nav>

<!-- A module's navigation, any depth. A leaf is a link; a group is a
     collapsible heading whose children render one step deeper through this
     very template. Groups holding the current screen open by themselves, the
     chevron overrides, and on the rail everything is shown at once — the
     flyout has no room for folding. Indentation follows `depth`. -->
<ng-template
  #tree
  let-items="items"
  let-moduleId="moduleId"
  let-depth="depth"
  let-path="path"
  let-heading="heading"
>
  @for (item of items; track entryKey(item)) {
    <!-- An entry is draggable only directly under a heading: that is the one
         list the arrangement orders. Deeper entries belong to the module's
         own structure and are not the user's to reorder. -->
    @let movable =
      heading !== undefined &&
      !heading.entriesFixed &&
      depth === 0 &&
      canMoveEntries(moduleId);
    @let panel = 'entry:' + moduleId + '/' + entryKey(item);

    @if (item.route !== undefined) {
      <li
        class="sidebar__entry"
        [class.sidebar__entry--dragging]="dragging() === entryKey(item)"
        [class.sidebar__entry--drop-before]="isDropBefore(item)"
        [class.sidebar__entry--drop-after]="isDropAfter(item)"
        [attr.draggable]="movable ? true : null"
        (dragstart)="onDragStart($event, item)"
        (dragend)="onDragEnd()"
        (dragover)="heading && onDragOverEntry($event, item, heading.id)"
        (drop)="heading && onDrop($event, moduleId, heading)"
      >
        <!-- `draggable="false"` on the link, always: a browser drags an <a> as
             a URL by itself, which swallows the row's own dragstart — the
             insertion bar never appears and the drop lands wherever the
             heading says, not where the pointer is. -->
        <a
          class="sidebar__link sidebar__link--sub"
          [class.sidebar__link--movable]="movable"
          draggable="false"
          [style.--depth]="depth"
          [attr.data-accent]="accentOf(item, heading)"
          [routerLink]="link(moduleId, item.route)"
          routerLinkActive="sidebar__link--active"
          [routerLinkActiveOptions]="{ exact: item.route === '' }"
          (click)="onDestinationPicked(moduleId)"
        >
          @if (item.icon; as icon) {
            <i class="sidebar__icon" [class]="icon" aria-hidden="true"></i>
          }
          <span class="sidebar__text">{{ item.labelKey | translate }}</span>
          @if (badgeOf(item); as count) {
            <span class="sidebar__badge">{{ count }}</span>
          }
        </a>

        <!-- The grip: the pointer-free way to the same moves, and the only
             arrange affordance on an entry. It appears on hover or focus and
             sits over the leading edge rather than reserving a gutter — the
             column cannot spare width for a control used once a month. -->
        @if (movable) {
          <button
            type="button"
            class="sidebar__arrange-trigger sidebar__grip"
            [attr.aria-expanded]="isPanelOpen(panel)"
            [attr.aria-label]="'sidebar.arrange.entry' | translate"
            (click)="togglePanel(panel, $event)"
          >
            <i class="pi pi-bars" aria-hidden="true"></i>
          </button>
        }

        @if (movable && isPanelOpen(panel) && heading) {
          <!-- Inline, not a popover: a popover over a 15.5rem column covers
               the very list the user is reasoning about. -->
          <div class="sidebar__arrange" role="group">
            <div class="sidebar__arrange-row">
              <button
                type="button"
                class="sidebar__arrange-action"
                [disabled]="isFirstIn(heading, item)"
                (click)="moveEntry(moduleId, entryKey(item), heading.id, indexIn(heading, item) - 1)"
              >
                <i class="pi pi-arrow-up" aria-hidden="true"></i>
                {{ 'sidebar.arrange.up' | translate }}
              </button>
              <button
                type="button"
                class="sidebar__arrange-action"
                [disabled]="isLastIn(heading, item)"
                (click)="moveEntry(moduleId, entryKey(item), heading.id, indexIn(heading, item) + 2)"
              >
                <i class="pi pi-arrow-down" aria-hidden="true"></i>
                {{ 'sidebar.arrange.down' | translate }}
              </button>
            </div>

            <span class="sidebar__arrange-caption">
              {{ 'sidebar.arrange.moveTo' | translate }}
            </span>
            @for (target of headingsOfModule(moduleId); track target.id) {
              @if (target.id !== heading.id) {
                <button
                  type="button"
                  class="sidebar__arrange-action"
                  (click)="moveEntry(moduleId, entryKey(item), target.id, target.entries.length)"
                >
                  {{ isUntitled(target) ? ('sidebar.arrange.untitled' | translate) : target.title }}
                </button>
              }
            }
          </div>
        }
      </li>
    } @else {
      <!-- `groupKey` keys the fold by the entry's id under its path — never by
           its label, which repeats across the catalogue. -->
      @let key = groupKey(path, item);
      <!-- A grouping position directly under a heading is a position of the
           arranged menu like any other — "Dispoplan" and "Konstanten" sit
           beside "Kundenstamm" and the user may put them anywhere. Only its
           CHILDREN belong to the module's structure, and those stay put. -->
      <li
        class="sidebar__group"
        [class.sidebar__entry--dragging]="dragging() === entryKey(item)"
        [class.sidebar__group--drop-before]="isDropBefore(item)"
        [class.sidebar__group--drop-after]="isDropAfter(item)"
        [attr.draggable]="movable ? true : null"
        (dragstart)="onDragStart($event, item)"
        (dragend)="onDragEnd()"
        (dragover)="heading && onDragOverEntry($event, item, heading.id)"
        (drop)="heading && onDrop($event, moduleId, heading)"
      >
        <button
          type="button"
          class="sidebar__group-label"
          [class.sidebar__link--movable]="movable"
          [style.--depth]="depth"
          [attr.data-accent]="accentOf(item, heading)"
          [attr.aria-expanded]="groupOpen(moduleId, key, item)"
          (click)="toggleGroup(key, moduleId, item)"
        >
          @if (item.icon; as icon) {
            <i class="sidebar__icon" [class]="icon" aria-hidden="true"></i>
          }
          <span class="sidebar__text">{{ item.labelKey | translate }}</span>
          @if (!rail()) {
            <i
              class="sidebar__group-chevron pi"
              [class.pi-chevron-down]="groupOpen(moduleId, key, item)"
              [class.pi-chevron-right]="!groupOpen(moduleId, key, item)"
              aria-hidden="true"
            ></i>
          }
        </button>

        <!-- The same grip as a routed entry: a grouping position is moved the
             same way, and the keyboard needs the panel because a group's own
             row already answers click with folding. -->
        @if (movable) {
          <button
            type="button"
            class="sidebar__arrange-trigger sidebar__grip"
            [attr.aria-expanded]="isPanelOpen(panel)"
            [attr.aria-label]="'sidebar.arrange.entry' | translate"
            (click)="togglePanel(panel, $event)"
          >
            <i class="pi pi-bars" aria-hidden="true"></i>
          </button>
        }

        @if (movable && isPanelOpen(panel) && heading) {
          <div class="sidebar__arrange" role="group">
            <div class="sidebar__arrange-row">
              <button
                type="button"
                class="sidebar__arrange-action"
                [disabled]="isFirstIn(heading, item)"
                (click)="moveEntry(moduleId, entryKey(item), heading.id, indexIn(heading, item) - 1)"
              >
                <i class="pi pi-arrow-up" aria-hidden="true"></i>
                {{ 'sidebar.arrange.up' | translate }}
              </button>
              <button
                type="button"
                class="sidebar__arrange-action"
                [disabled]="isLastIn(heading, item)"
                (click)="moveEntry(moduleId, entryKey(item), heading.id, indexIn(heading, item) + 2)"
              >
                <i class="pi pi-arrow-down" aria-hidden="true"></i>
                {{ 'sidebar.arrange.down' | translate }}
              </button>
            </div>

            <span class="sidebar__arrange-caption">
              {{ 'sidebar.arrange.moveTo' | translate }}
            </span>
            @for (target of headingsOfModule(moduleId); track target.id) {
              @if (target.id !== heading.id) {
                <button
                  type="button"
                  class="sidebar__arrange-action"
                  (click)="moveEntry(moduleId, entryKey(item), target.id, target.entries.length)"
                >
                  {{ isUntitled(target) ? ('sidebar.arrange.untitled' | translate) : target.title }}
                </button>
              }
            }
          </div>
        }

        @if (groupOpen(moduleId, key, item)) {
          <ul class="sidebar__list sidebar__list--sub">
            <ng-container
              *ngTemplateOutlet="
                tree;
                context: {
                  items: item.children ?? [],
                  moduleId: moduleId,
                  depth: depth + 1,
                  path: key,
                  heading: heading,
                }
              "
            />
          </ul>
        }
      </li>
    }
  }
</ng-template>

<!-- Who is signed in, the other Saphir surfaces, and the way out — a menu
     behind the person's row. Renders nothing without a session. -->
<app-user-menu [rail]="rail()" />
